import * as React from "react";
import { useTranslation } from "react-i18next";
import { usePostHog } from "posthog-js/react";
import {
  Outlet,
  createRootRoute,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import * as Sentry from "@sentry/react";
import { auth } from "../config/firebase";
import {
  onAuthStateChanged,
  sendEmailVerification,
  signOut,
  User,
} from "firebase/auth";
import { useListBrandTemplates } from "@dataconnect/generated/react";
import { initializeUser } from "../services/userService";
import { AppToaster } from "../components/AppToaster";
import { TrialBanner } from "../components/TrialBanner";
import { SubscriptionModal } from "../components/SubscriptionModal";
import { OutOfCreditsOverlay } from "../components/OutOfCreditsOverlay";
import { ImpersonationBanner } from "../components/ImpersonationBanner";
import { useGetUserCredits } from "@dataconnect/generated/react";
import { UserStatus } from "@dataconnect/generated";
import { useImpersonation } from "../hooks/useImpersonation";
import { ImpersonationContext } from "../context/ImpersonationContext";
import { captureReferralFromUrl } from "../services/referralService";
import { resolveRootRouteState } from "../utils/rootRouteGuard";

export const Route = createRootRoute({
  component: RootComponent,
});

// Capture `?ref=XXX` as early as possible — once at module load so it lands in
// localStorage even if the user never logs in on this visit. Subsequent
// navigations don't re-trigger this; that's intentional, since the URL param
// is only meaningful on the original landing.
captureReferralFromUrl();

// Shared help-article links (/help#article-id) survive the login redirect via
// this sessionStorage key: stashed before bouncing to /login, restored after.
const PENDING_HELP_ARTICLE_KEY = "pendingHelpArticle";

function RootComponent() {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = React.useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = React.useState(true);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] =
    React.useState(false);
  const [isOutOfCreditsOverlayOpen, setIsOutOfCreditsOverlayOpen] =
    React.useState(false);
  const hasAutoShownOutOfCreditsRef = React.useRef(false);
  const impersonation = useImpersonation();

  const { data: creditsData } = useGetUserCredits({
    enabled: !!user && !loadingAuth,
  });
  const userCredits = creditsData?.user;
  // CANCELED users always need to re-subscribe before they can generate
  // again — treat them like a trial user who has run out of credits so the
  // OutOfCreditsOverlay auto-opens on first session and the TrialBanner
  // routes them straight to the pricing table.
  const isOutOfCredits =
    (userCredits?.status === UserStatus.TRIAL &&
      userCredits?.credits !== undefined &&
      userCredits.credits <= 0) ||
    userCredits?.status === UserStatus.CANCELED;

  React.useEffect(() => {
    if (isOutOfCredits && !hasAutoShownOutOfCreditsRef.current) {
      hasAutoShownOutOfCreditsRef.current = true;
      setIsOutOfCreditsOverlayOpen(true);
    }
  }, [isOutOfCredits]);

  const handleBannerActivate = React.useCallback(() => {
    if (isOutOfCredits) {
      setIsOutOfCreditsOverlayOpen(true);
    } else {
      setIsSubscriptionModalOpen(true);
    }
  }, [isOutOfCredits]);

  const {
    data: brandsData,
    isLoading: isLoadingBrands,
    isError: isBrandsError,
    refetch: refetchBrands,
  } = useListBrandTemplates({
    enabled: !!user && !loadingAuth, // Only fetch when user is logged in
  });

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        setUser(currentUser);
        if (currentUser) {
          const tokenResult = await currentUser.getIdTokenResult();
          const isImpersonating = !!tokenResult.claims.impersonatedBy;
          if (!isImpersonating) {
            posthog?.identify(currentUser.uid, {
              email: currentUser.email,
              name: currentUser.displayName,
              auth_provider:
                currentUser.providerData[0]?.providerId ?? "unknown",
              created_at: currentUser.metadata.creationTime,
            });
          }
          Sentry.setUser({
            id: currentUser.uid,
            email: currentUser.email ?? undefined,
          });
          if (!isImpersonating) {
            try {
              await initializeUser(currentUser);
            } catch (error) {
              console.error("Failed to initialize user:", error);
            }
          }
        } else {
          // Reset when user logs out
          Sentry.setUser(null);
        }
      } catch (error) {
        // Token retrieval can reject on a stale session; never let that keep
        // the app on the loading screen.
        console.error("Failed to handle auth state change:", error);
      } finally {
        setLoadingAuth(false);
      }
    });
    return () => unsubscribe();
  }, [posthog]);

  // Enrich PostHog person properties when subscription status is known
  React.useEffect(() => {
    if (user && userCredits?.status) {
      posthog?.people?.set({ subscription_status: userCredits.status });
    }
  }, [user, userCredits?.status, posthog]);

  const optedOutForImpersonation = React.useRef(false);
  React.useEffect(() => {
    if (!posthog) return;
    if (impersonation.isImpersonating) {
      optedOutForImpersonation.current = true;
      posthog.opt_out_capturing();
    } else if (optedOutForImpersonation.current) {
      optedOutForImpersonation.current = false;
      posthog.opt_in_capturing();
    }
  }, [posthog, impersonation.isImpersonating]);

  React.useEffect(() => {
    if (loadingAuth) return;

    const isOnLoginPage = location.pathname === "/login";
    const isOnOnboardingPage = location.pathname === "/onboarding";
    const isOnAuthCallback = location.pathname === "/auth/callback";
    const isOnShopVibesCallback = location.pathname === "/shopvibes/callback";

    // Rule 1: Not logged in → redirect to /login (except auth callback)
    if (!user) {
      if (!isOnLoginPage && !isOnAuthCallback) {
        if (location.pathname === "/help" && window.location.hash) {
          sessionStorage.setItem(
            PENDING_HELP_ARTICLE_KEY,
            window.location.hash.slice(1)
          );
        }
        navigate({ to: "/login", search: {} });
      }
      return;
    }

    // User is logged in, wait for brand data to actually load
    if (isLoadingBrands || !brandsData) return;

    const hasTemplates = brandsData.brandTemplates.length > 0;

    // When user has templates and is on onboarding with returnTab=library, they clicked "Add New Brand" — allow them to stay
    const onboardingAddBrandSearch =
      (location.search as { returnTab?: string } | undefined)?.returnTab ??
      null;
    const isOnboardingToAddBrand =
      isOnOnboardingPage && onboardingAddBrandSearch === "library";

    // Rule 2: Logged in but no brand templates → onboarding is mandatory
    // (except on the ShopVibes OAuth callback, which would otherwise be
    // bounced to /onboarding before it can finish the connect flow — the
    // authorization code is single-use, so that silently loses the link)
    if (!hasTemplates && !isOnShopVibesCallback) {
      if (!isOnOnboardingPage || onboardingAddBrandSearch === "library") {
        navigate({ to: "/onboarding", search: { returnTab: undefined } });
      }
      return;
    }

    // Rule 3: Logged in with templates → redirect away from login/onboarding/auth callback (except when adding another brand)
    if (
      isOnLoginPage ||
      isOnAuthCallback ||
      (isOnOnboardingPage && !isOnboardingToAddBrand)
    ) {
      const pendingHelpArticle = sessionStorage.getItem(
        PENDING_HELP_ARTICLE_KEY
      );
      if (pendingHelpArticle) {
        sessionStorage.removeItem(PENDING_HELP_ARTICLE_KEY);
        navigate({ to: "/help", hash: pendingHelpArticle });
      } else {
        navigate({ to: "/", search: { returnTab: undefined } });
      }
    }
  }, [
    loadingAuth,
    user,
    isLoadingBrands,
    brandsData,
    navigate,
    location.pathname,
    location.search,
  ]);

  const needsEmailVerification =
    user &&
    !user.emailVerified &&
    user.providerData.some((p) => p.providerId === "password");

  const handleResendVerification = async () => {
    if (user) {
      await sendEmailVerification(user);
    }
  };

  const rootState = resolveRootRouteState({
    loadingAuth,
    hasUser: !!user,
    needsEmailVerification: !!needsEmailVerification,
    hasBrandsData: brandsData !== undefined,
    brandsError: isBrandsError,
  });

  if (rootState === "loading") {
    return (
      <div className="flex h-screen items-center justify-center">
        {t("common.loading")}
      </div>
    );
  }

  if (rootState === "error") {
    return (
      <div className="flex h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("appLoadError.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {t("appLoadError.description")}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => void refetchBrands()}
              className="rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-700"
            >
              {t("appLoadError.retry")}
            </button>
            <button
              type="button"
              onClick={() => void signOut(auth)}
              className="rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("login.signOutButton")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (needsEmailVerification) {
    return (
      <div className="flex h-screen items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-8 w-8 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("login.verifyEmailTitle")}
          </h2>
          <p className="text-gray-600">
            {t("login.verifyEmailDesc", { email: user.email })}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={async () => {
                await user.reload();
                if (user.emailVerified) {
                  window.location.reload();
                }
              }}
              className="w-full py-2.5 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              {t("login.iVerifiedMyEmail")}
            </button>
            <button
              onClick={handleResendVerification}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium"
            >
              {t("login.resendVerificationEmail")}
            </button>
            <button
              onClick={() => signOut(auth)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              {t("login.signOutButton")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ImpersonationContext.Provider value={impersonation}>
      <div className="flex flex-col h-screen w-full overflow-hidden">
        {impersonation.isImpersonating && (
          <ImpersonationBanner email={user?.email ?? null} />
        )}
        <TrialBanner onActivate={handleBannerActivate} user={user} />
        <div className="flex-1 w-full overflow-hidden flex flex-col relative">
          <Outlet />
        </div>
        <TanStackRouterDevtools position="bottom-right" />
        {isSubscriptionModalOpen && (
          <SubscriptionModal
            onClose={() => setIsSubscriptionModalOpen(false)}
          />
        )}
        {isOutOfCreditsOverlayOpen && (
          <OutOfCreditsOverlay
            onClose={() => setIsOutOfCreditsOverlayOpen(false)}
          />
        )}
      </div>
      <AppToaster />
    </ImpersonationContext.Provider>
  );
}
